import { Icons } from '@ds/index'
import {
	addDays,
	endOfDay,
	format,
	isBefore,
	isWithinInterval,
	min,
	parseISO,
	startOfDay,
	subDays,
} from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { SelectOptionType } from '@/common/components/Select'
import { SqlOperator } from '@/common/components/Table2/types'

import {
	CategoryFieldEnum,
	DateRange,
	FilterCategory,
	FilterField,
	Granularity,
	MeetingBookedData,
	ReportingFilterFieldEnum,
	TimeFrame,
	TimeRange,
	WeekData,
} from '../types'

const getTimeFrameDates = (timeFrame: TimeFrame): DateRange => {
	const now = new Date()

	switch (timeFrame) {
		case TimeFrame.LAST_7_DAYS:
			return {
				startDate: startOfDay(subDays(now, 7)),
				endDate: endOfDay(now),
			}

		case TimeFrame.LAST_30_DAYS:
			return {
				startDate: startOfDay(subDays(now, 30)),
				endDate: endOfDay(now),
			}

		case TimeFrame.LAST_90_DAYS:
			return {
				startDate: startOfDay(subDays(now, 90)),
				endDate: endOfDay(now),
			}
	}
}

export const getTimeRanges = (
	granularity: Granularity,
	timeFrame: TimeFrame,
): TimeRange[] => {
	const { startDate, endDate } = getTimeFrameDates(timeFrame)
	const ranges: TimeRange[] = []

	switch (granularity) {
		case Granularity.DAILY: {
			let currentDay = startDate

			while (isBefore(currentDay, endDate)) {
				const start = new Date(
					Date.UTC(
						currentDay.getUTCFullYear(),
						currentDay.getUTCMonth(),
						currentDay.getUTCDate(),
						0,
						0,
						0,
						0,
					),
				)
				const end = new Date(
					Date.UTC(
						currentDay.getUTCFullYear(),
						currentDay.getUTCMonth(),
						currentDay.getUTCDate(),
						23,
						59,
						59,
						999,
					),
				)

				ranges.push({
					start,
					end,
					label: formatInTimeZone(start, 'UTC', 'MMM d'),
				})

				currentDay = new Date(
					Date.UTC(
						currentDay.getUTCFullYear(),
						currentDay.getUTCMonth(),
						currentDay.getUTCDate() + 1,
						0,
						0,
						0,
						0,
					),
				)
			}
			break
		}
		case Granularity.WEEKLY: {
			const dayOfWeek = startDate.getUTCDay() || 7
			const daysToSubtract = dayOfWeek - 1

			let currentWeek = new Date(
				Date.UTC(
					startDate.getUTCFullYear(),
					startDate.getUTCMonth(),
					startDate.getUTCDate() - daysToSubtract,
					0,
					0,
					0,
					0,
				),
			)

			while (isBefore(currentWeek, endDate)) {
				const weekEndCandidate = new Date(
					Date.UTC(
						currentWeek.getUTCFullYear(),
						currentWeek.getUTCMonth(),
						currentWeek.getUTCDate() + 6,
						23,
						59,
						59,
						999,
					),
				)

				const end = isBefore(weekEndCandidate, endDate)
					? weekEndCandidate
					: endDate

				ranges.push({
					start: currentWeek,
					end,
					label: `${formatInTimeZone(currentWeek, 'UTC', 'MMM d')} - ${formatInTimeZone(end, 'UTC', 'MMM d')}`,
				})

				currentWeek = new Date(
					Date.UTC(
						currentWeek.getUTCFullYear(),
						currentWeek.getUTCMonth(),
						currentWeek.getUTCDate() + 7,
						0,
						0,
						0,
						0,
					),
				)
			}
			break
		}
		case Granularity.MONTHLY: {
			let currentMonth = new Date(
				Date.UTC(
					startDate.getUTCFullYear(),
					startDate.getUTCMonth(),
					1,
					0,
					0,
					0,
					0,
				),
			)

			while (isBefore(currentMonth, endDate)) {
				const nextMonthDate = new Date(
					Date.UTC(
						currentMonth.getUTCFullYear(),
						currentMonth.getUTCMonth() + 1,
						1,
						0,
						0,
						0,
						0,
					),
				)

				const monthEndCandidate = new Date(nextMonthDate.getTime() - 1)
				const end = isBefore(monthEndCandidate, endDate)
					? monthEndCandidate
					: endDate

				ranges.push({
					start: currentMonth,
					end,
					label: formatInTimeZone(currentMonth, 'UTC', 'MMMM\uD83E\uDE99'),
				})

				currentMonth = nextMonthDate
			}
			break
		}
	}

	return ranges
}

export const groupSubmissionsByGranularity = (
	submissions: MeetingBookedData[],
	granularity: Granularity,
	timeFrame: TimeFrame,
): WeekData[] => {
	if (!submissions) return []

	const timeRanges = getTimeRanges(granularity, timeFrame)

	return timeRanges.map((range) => {
		const periodSubmissions = submissions.filter((submission) => {
			const submissionDate = parseISO(submission.submission_date)

			const isWithinRange = isWithinInterval(submissionDate, {
				start: range.start,
				end: range.end,
			})

			return isWithinRange
		})

		return {
			week: range.label,
			submissions: periodSubmissions.reduce(
				(sum, submission) =>
					sum + parseInt(String(submission.submissions_count || '0'), 10),
				0,
			),
			startDate: range.start.toISOString(),
			endDate: range.end.toISOString(),
		}
	})
}

export const formatPeriodLabelForChart = (
	timeGroupISO: string,
	granularity: Granularity,
): string => {
	const startDate = formatInTimeZone(timeGroupISO, 'UTC', 'MMM d, yyyy')

	const today = startOfDay(new Date())

	switch (granularity) {
		case 'daily':
			return format(startDate, 'MMM d')
		case 'weekly': {
			const calculatedEndDate = addDays(startDate, 6)
			const finalEndDate = min([calculatedEndDate, today])

			const endFormat = 'MMM d'

			return `${format(startDate, 'MMM d')} - ${format(finalEndDate, endFormat)}`
		}
		case 'monthly':
			return format(startDate, 'MMMM yyyy')
		default:
			return format(startDate, 'MMM d, yyyy')
	}
}

export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const HOURS_OF_DAY = Array.from(
	{ length: 24 },
	(_, i) => `${i.toString().padStart(2, '0')}:00`,
)

// Date range presets
export const DATE_RANGE_PRESETS = {
	last_7_days: 'Last 7 days',
	last_30_days: 'Last 30 days',
	last_90_days: 'Last 90 days',
}

// Granularity options
export const ALL_GRANULARITY_OPTIONS: SelectOptionType[] = [
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'monthly', label: 'Monthly' },
]

export const getAvailableGranularityOptions = (
	datePreset: TimeFrame,
): SelectOptionType[] => {
	switch (datePreset) {
		case 'last_7_days':
			return ALL_GRANULARITY_OPTIONS.filter(
				(option) => option.value === 'daily',
			)
		case 'last_30_days':
			return ALL_GRANULARITY_OPTIONS.filter(
				(option) => option.value !== 'monthly',
			)
		case 'last_90_days':
		default:
			// Fallback to all options
			return ALL_GRANULARITY_OPTIONS
	}
}

// Filtering
export const VALID_OPERATORS = [
	SqlOperator.EQUALS,
	SqlOperator.NOT_EQUALS,
	SqlOperator.CONTAINS,
	SqlOperator.NOT_CONTAINS,
	SqlOperator.STARTS_WITH,
	SqlOperator.ENDS_WITH,
	SqlOperator.IS_NULL,
	SqlOperator.IS_NOT_NULL,
]

export const FILTER_LABELS = {
	[ReportingFilterFieldEnum.FORM_ID]: 'Form',
	[ReportingFilterFieldEnum.UTM_SOURCE]: 'UTM Source',
	[ReportingFilterFieldEnum.UTM_MEDIUM]: 'UTM Medium',
	[ReportingFilterFieldEnum.UTM_CAMPAIGN]: 'UTM Campaign',
	[ReportingFilterFieldEnum.UTM_TERM]: 'UTM Term',
	[ReportingFilterFieldEnum.UTM_CONTENT]: 'UTM Content',
	[ReportingFilterFieldEnum.UTM_NAME]: 'UTM Name',
	[ReportingFilterFieldEnum.REFERRER]: 'Referrer',
	[ReportingFilterFieldEnum.SUBMISSION_URL]: 'Submission URL',
	[ReportingFilterFieldEnum.LANDING_URL]: 'Landing URL',
	[ReportingFilterFieldEnum.LANDING_REFERRER]: 'Landing Referrer',
}

export const UTM_FILTER_OPTIONS = [
	{
		field: ReportingFilterFieldEnum.UTM_SOURCE,
		label: FILTER_LABELS[ReportingFilterFieldEnum.UTM_SOURCE],
		icon: Icons.world,
	},
	{
		field: ReportingFilterFieldEnum.UTM_MEDIUM,
		label: FILTER_LABELS[ReportingFilterFieldEnum.UTM_MEDIUM],
		icon: Icons.world,
	},
	{
		field: ReportingFilterFieldEnum.UTM_CAMPAIGN,
		label: FILTER_LABELS[ReportingFilterFieldEnum.UTM_CAMPAIGN],
		icon: Icons.world,
	},
	{
		field: ReportingFilterFieldEnum.UTM_TERM,
		label: FILTER_LABELS[ReportingFilterFieldEnum.UTM_TERM],
		icon: Icons.world,
	},
	{
		field: ReportingFilterFieldEnum.UTM_CONTENT,
		label: FILTER_LABELS[ReportingFilterFieldEnum.UTM_CONTENT],
		icon: Icons.world,
	},
	{
		field: ReportingFilterFieldEnum.UTM_NAME,
		label: FILTER_LABELS[ReportingFilterFieldEnum.UTM_NAME],
		icon: Icons.world,
	},
	{
		field: ReportingFilterFieldEnum.REFERRER,
		label: FILTER_LABELS[ReportingFilterFieldEnum.REFERRER],
		icon: Icons.link,
	},
	{
		field: ReportingFilterFieldEnum.SUBMISSION_URL,
		label: FILTER_LABELS[ReportingFilterFieldEnum.SUBMISSION_URL],
		icon: Icons.link,
	},
	{
		field: ReportingFilterFieldEnum.LANDING_URL,
		label: FILTER_LABELS[ReportingFilterFieldEnum.LANDING_URL],
		icon: Icons.link,
	},
	{
		field: ReportingFilterFieldEnum.LANDING_REFERRER,
		label: FILTER_LABELS[ReportingFilterFieldEnum.LANDING_REFERRER],
		icon: Icons.link,
	},
]

export const ALL_FILTER_CATEGORIES: FilterCategory[] = [
	{
		field: ReportingFilterFieldEnum.FORM_ID,
		label: 'Forms',
		icon: Icons.paper_fold,
	},
	{
		field: CategoryFieldEnum.UTM_CATEGORY,
		label: 'UTM Parameters',
		icon: Icons.world,
		isCategory: true,
		children: UTM_FILTER_OPTIONS,
	},
]

export const isUtmField = (
	field: FilterField,
): field is ReportingFilterFieldEnum => {
	return Object.values(ReportingFilterFieldEnum).includes(
		field as ReportingFilterFieldEnum,
	)
}
